import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingDownIcon, TrendingUpIcon, DollarSignIcon, ZapIcon, ShieldIcon, RocketIcon } from 'lucide-react';
function AnimatedNumber({
  target,
  suffix = '',
  prefix = '',
  decimals = 0
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    const start = Date.now();
    const duration = 1800;
    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(parseFloat((eased * target).toFixed(decimals)));
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target, decimals]);
  return <span ref={ref} data-id="element-51">
      {prefix}
      {decimals > 0 ? count.toFixed(decimals) : Math.round(count)}
      {suffix}
    </span>;
}
const scorecardItems = [{
  icon: ZapIcon,
  label: 'AI-Assisted Cycle Time Reduction',
  value: 2.3,
  suffix: 'd',
  prefix: '-',
  decimals: 1,
  trend: 'positive',
  description: 'Average days saved per feature shipped',
  color: '#00D4FF',
  bgColor: 'rgba(0, 212, 255, 0.08)',
  borderColor: 'rgba(0, 212, 255, 0.2)'
}, {
  icon: RocketIcon,
  label: 'Deployment Frequency Increase',
  value: 31,
  suffix: '%',
  prefix: '+',
  decimals: 0,
  trend: 'positive',
  description: 'More deployments per sprint with AI',
  color: '#10B981',
  bgColor: 'rgba(16, 185, 129, 0.08)',
  borderColor: 'rgba(16, 185, 129, 0.2)'
}, {
  icon: ShieldIcon,
  label: 'Incident Rate Delta',
  value: 18,
  suffix: '%',
  prefix: '-',
  decimals: 0,
  trend: 'positive',
  description: 'Fewer incidents on AI-assisted features',
  color: '#3B82F6',
  bgColor: 'rgba(59, 130, 246, 0.08)',
  borderColor: 'rgba(59, 130, 246, 0.2)'
}, {
  icon: DollarSignIcon,
  label: 'Cost per Feature Shipped',
  value: 23,
  suffix: '%',
  prefix: '-',
  decimals: 0,
  trend: 'positive',
  description: 'Engineering cost reduction per feature',
  color: '#F59E0B',
  bgColor: 'rgba(245, 158, 11, 0.08)',
  borderColor: 'rgba(245, 158, 11, 0.2)'
}];
export function AIScorecardSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-24 px-6 bg-[#080C14] relative" data-id="element-52">
      <div className="max-w-6xl mx-auto" data-id="element-53">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-4" data-id="element-54">
          <span className="text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-55">
            Track AI ROI
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.1,
        duration: 0.6
      }} className="mb-4" data-id="element-56">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-3" data-id="element-57">
            The Executive AI Scorecard
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mb-2" data-id="element-58">
            For the first time — measurable business impact from AI investment.
          </p>
          <p className="text-cyan-400 font-medium" data-id="element-59">
            This is not AI adoption reporting. This is measurable business
            impact.
          </p>
        </motion.div>

        {/* Big statement */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.2,
        duration: 0.6
      }} className="mb-12 p-6 rounded-2xl border border-[#1E2D45] bg-[#0D1421]" data-id="element-60">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4" data-id="element-61">
            <div className="flex-1" data-id="element-62">
              <p className="text-gray-300 text-base leading-relaxed" data-id="element-63">
                Engineering leaders spend millions on AI tools but can't answer
                the most basic question:{' '}
                <span className="text-white font-semibold" data-id="element-64">Is it working?</span>{' '}
                Waydev's AI Scorecard connects AI activity to business outcomes
                — cycle time, deployment frequency, incident rates, and
                engineering cost.
              </p>
            </div>
            <div className="flex-shrink-0 px-6 py-4 rounded-xl text-center" style={{
            background: 'linear-gradient(135deg, rgba(0,212,255,0.1) 0%, rgba(59,130,246,0.1) 100%)',
            border: '1px solid rgba(0,212,255,0.2)'
          }} data-id="element-65">
              <div className="text-4xl font-black text-cyan-400 mb-1" data-id="element-66">
                <AnimatedNumber target={4.2} suffix="x" decimals={1} data-id="element-67" />
              </div>
              <div className="text-xs text-gray-400" data-id="element-68">Average ROI</div>
              <div className="text-xs text-gray-600" data-id="element-69">on AI tooling</div>
            </div>
          </div>
        </motion.div>

        {/* Scorecard grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-id="element-70">
          {scorecardItems.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.label} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            delay: 0.3 + i * 0.1,
            duration: 0.5
          }} className="rounded-2xl p-6 border" style={{
            backgroundColor: item.bgColor,
            borderColor: item.borderColor
          }} data-id="element-71">
                <div className="flex items-start justify-between mb-4" data-id="element-72">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                backgroundColor: `${item.color}20`
              }} data-id="element-73">
                    <Icon className="w-5 h-5" style={{
                  color: item.color
                }} data-id="element-74" />
                  </div>
                  <TrendingDownIcon className="w-4 h-4 text-green-400" data-id="element-75" />
                </div>

                <div className="text-5xl font-black mb-2" style={{
              color: item.color
            }} data-id="element-76">
                  <AnimatedNumber target={item.value} suffix={item.suffix} prefix={item.prefix} decimals={item.decimals} data-id="element-77" />
                </div>

                <div className="text-white font-semibold text-sm mb-1" data-id="element-78">
                  {item.label}
                </div>
                <div className="text-gray-500 text-xs" data-id="element-79">{item.description}</div>
              </motion.div>;
        })}
        </div>

        {/* Cost vs productivity */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.7,
        duration: 0.6
      }} className="bg-[#0D1421] border border-[#1E2D45] rounded-2xl p-6" data-id="element-80">
          <div className="text-white font-semibold mb-2" data-id="element-81">
            Engineering Cost vs AI Productivity Gain
          </div>
          <div className="text-gray-500 text-sm mb-6" data-id="element-82">
            Quarterly trend — cost efficiency improving as AI adoption matures
          </div>

          <div className="space-y-4" data-id="element-83">
            {[{
            label: 'Q1 2024',
            cost: 100,
            gain: 112,
            costLabel: '$100K',
            gainLabel: '$112K gain'
          }, {
            label: 'Q2 2024',
            cost: 100,
            gain: 134,
            costLabel: '$100K',
            gainLabel: '$134K gain'
          }, {
            label: 'Q3 2024',
            cost: 100,
            gain: 158,
            costLabel: '$100K',
            gainLabel: '$158K gain'
          }, {
            label: 'Q4 2024',
            cost: 100,
            gain: 189,
            costLabel: '$100K',
            gainLabel: '$189K gain'
          }].map((q, i) => <div key={q.label} className="flex items-center gap-4" data-id="element-84">
                <span className="text-gray-500 text-xs w-14 flex-shrink-0" data-id="element-85">
                  {q.label}
                </span>
                <div className="flex-1 flex gap-1 items-center" data-id="element-86">
                  <motion.div className="h-5 rounded flex items-center justify-end pr-2" style={{
                backgroundColor: '#1E2D45',
                width: '40%'
              }} initial={{
                opacity: 0
              }} animate={isInView ? {
                opacity: 1
              } : {}} transition={{
                delay: 0.8 + i * 0.1
              }} data-id="element-87">
                    <span className="text-xs text-gray-400" data-id="element-88">{q.costLabel}</span>
                  </motion.div>
                  <motion.div className="h-5 rounded flex items-center justify-end pr-2" style={{
                backgroundColor: '#00D4FF20',
                borderLeft: '2px solid #00D4FF'
              }} initial={{
                width: 0
              }} animate={isInView ? {
                width: `${q.gain / 200 * 60}%`
              } : {}} transition={{
                delay: 0.9 + i * 0.1,
                duration: 0.6,
                ease: 'easeOut'
              }} data-id="element-89">
                    <span className="text-xs text-cyan-400 whitespace-nowrap" data-id="element-90">
                      {q.gainLabel}
                    </span>
                  </motion.div>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}